<template>
  <div class="live-requests-wrapper" v-if="visibleRequests.length > 0">
    <!-- Main request card (normal statuses only) -->
    <LiveRequestItem
      v-if="mainRequest && mainRequest.id"
      :request="mainRequest"
      :is-main-request="true"
      @resubmitted="handleResubmitted"
      @hide="handleHideRequest"
    />

    <!-- Additional normal requests -->
    <template v-if="additionalNormalRequests.length > 0">
      <LiveRequestItem
        v-for="request in additionalNormalRequests"
        :key="request.id"
        :request="request"
        :is-main-request="false"
        @resubmitted="handleResubmitted"
        @hide="handleHideRequest"
      />
    </template>

    <!-- Dropdown for 'requested' status only -->
    <div v-if="dropdownRequests.length > 0" class="additional-requests">
      <button
        class="show-more-btn"
        @click="toggleAdditionalRequests"
        :class="{ expanded: showAdditionalRequests, 'has-attention': resubmitCount > 0 }"
      >
        <span v-if="!showAdditionalRequests">
          {{
            $t("dashboard.live_request_show_more", "Campaign go live pending requests ({count})", {
              count: dropdownRequests.length,
            })
          }}
          <span v-if="resubmitCount > 0" class="attention-badge">
            {{ resubmitCount }}
          </span>
        </span>
        <span v-else>
          {{ $t("dashboard.live_request_show_less", "Show less") }}
        </span>
        <svg
          class="chevron-icon"
          :class="{ rotated: showAdditionalRequests }"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
        </svg>
      </button>

      <transition name="slide-down">
        <div v-show="showAdditionalRequests" class="additional-requests-list">
          <LiveRequestItem
            v-for="request in dropdownRequests"
            :key="request.id"
            :request="request"
            :is-main-request="false"
            @resubmitted="handleResubmitted"
            @hide="handleHideRequest"
          />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import LiveRequestItem from "./live-request-item.vue";

export default defineComponent({
  name: "LiveRequestsCard",

  components: {
    LiveRequestItem,
  },

  props: {
    liveRequests: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      showAdditionalRequests: false,
      hiddenRequestIds: this.loadHiddenRequestIds(),
    };
  },

  computed: {
    visibleRequests() {
      // Filter out hidden requests
      return this.liveRequests.filter((request) => !this.hiddenRequestIds.includes(request.id));
    },

    // Requests with status 'requested' go into dropdown
    dropdownRequests() {
      return this.visibleRequests.filter((request) => request.status === "requested");
    },

    // All other statuses display normally
    normalRequests() {
      return this.visibleRequests.filter((request) => request.status !== "requested");
    },

    mainRequest() {
      return this.normalRequests[0] || null;
    },

    additionalNormalRequests() {
      return this.normalRequests.slice(1);
    },

    resubmitCount() {
      return this.dropdownRequests.filter((request) => request.status === "resubmit").length;
    },
  },

  methods: {
    toggleAdditionalRequests() {
      this.showAdditionalRequests = !this.showAdditionalRequests;
    },

    handleResubmitted() {
      // Optionally show a success notification
      // The store action will refresh the list automatically
    },

    loadHiddenRequestIds() {
      try {
        const stored = localStorage.getItem("hiddenLiveRequestIds");
        return stored ? JSON.parse(stored) : [];
      } catch (error) {
        console.error("Failed to load hidden request IDs:", error);
        return [];
      }
    },

    saveHiddenRequestIds() {
      try {
        localStorage.setItem("hiddenLiveRequestIds", JSON.stringify(this.hiddenRequestIds));
      } catch (error) {
        console.error("Failed to save hidden request IDs:", error);
      }
    },

    handleHideRequest(requestId) {
      if (!this.hiddenRequestIds.includes(requestId)) {
        this.hiddenRequestIds.push(requestId);
        this.saveHiddenRequestIds();
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.live-requests-wrapper {
  margin-bottom: 24px;

  .additional-requests {
    margin-top: 16px;

    .show-more-btn {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding: 12px 16px;
      background: #f8f9fa;
      border: 1px solid #e9ecef;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.3s ease;
      font-size: 14px;
      font-weight: 500;
      color: #107598;

      &.has-attention {
        border-color: #fd7e14;
        background: #fff5e6;

        &:hover {
          background: #ffe5d0;
          border-color: #fd7e14;
        }
      }

      &:hover {
        background: #e9ecef;
        border-color: #107598;
      }

      &:focus {
        outline: none;
        box-shadow: 0 0 0 3px rgba(16, 117, 152, 0.1);
      }

      .attention-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 20px;
        height: 20px;
        padding: 0 6px;
        margin-left: 8px;
        background: #fd7e14;
        color: #ffffff;
        border-radius: 10px;
        font-size: 12px;
        font-weight: 600;
        line-height: 1;
      }

      .chevron-icon {
        width: 20px;
        height: 20px;
        transition: transform 0.3s ease;

        &.rotated {
          transform: rotate(180deg);
        }
      }
    }

    .additional-requests-list {
      margin-top: 12px;
    }
  }
}

// Animation for slide down
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.slide-down-enter-from,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
  margin-top: 0;
}

.slide-down-enter-to,
.slide-down-leave-from {
  max-height: 1000px;
  opacity: 1;
  margin-top: 12px;
}

@media (max-width: 768px) {
  .live-requests-wrapper {
    .card-content {
      padding: 16px;
    }

    .card-header {
      padding: 12px 16px;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;

      .card-title {
        font-size: 16px;
      }
    }

    .request-info {
      .info-row {
        flex-direction: column;
        gap: 4px;

        .label {
          min-width: auto;
          margin-right: 0;
        }
      }
    }
  }
}
</style>
